import { FC, useEffect } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import { checkAHAVI, replaceHtmlToStr, showWarning } from 'utils/utils';
import { ClaudeProposalHotelInfo } from 'ts/shared/interfaces/ProposalData';
import {
  ClaudeHotelInfoConfig,
  createDefaultClaudeHotelInfoConfig,
  formatClaudeTextValue,
  getClaudeI18nLanguage,
  getClaudeLanguageCountryCode,
  getClaudeLanguageShortLabel,
  getDefaultClaudeLanguage,
  isClaudeEnglishLanguage,
} from 'utils/shared/claudeProposalInformation';
import { i18nClaude } from 'utils/shared/utils';
import LoadingBlock from '../../LoadingBlock';
import { imgUs } from 'constants/generic';

interface ProposalClaudeHotelInformationProps {
  claudeData?: ClaudeProposalHotelInfo;
  config?: ClaudeHotelInfoConfig;
  onChangeConfig?: (config: ClaudeHotelInfoConfig) => void;
  activeLanguage?: string;
  onActiveLanguageChange?: (language: string) => void;
}

const ProposalClaudeHotelInformation: FC<ProposalClaudeHotelInformationProps> = ({
  claudeData,
  config,
  onChangeConfig,
  activeLanguage,
  onActiveLanguageChange,
}) => {
  
  const { t, i18n: claudeI18n } = useTranslation(undefined, { i18n: i18nClaude });
  const content = claudeData?.content || [];
  const currentConfig =
    config || createDefaultClaudeHotelInfoConfig(content);
  const selectedLanguage =
    activeLanguage || config?.language || getDefaultClaudeLanguage(content);
  const isRtl = getClaudeI18nLanguage(selectedLanguage) === 'he';
  const imagesIndexes = currentConfig.imagesIndexes;

  useEffect(() => {
    if (!claudeData?.content?.length || config) {
      return;
    }
    const defaultLang = getDefaultClaudeLanguage(claudeData.content);
    onActiveLanguageChange?.(defaultLang);
    onChangeConfig?.(createDefaultClaudeHotelInfoConfig(claudeData.content));
  }, [claudeData, config, onChangeConfig, onActiveLanguageChange]);

  useEffect(() => {
    if (selectedLanguage) {
      claudeI18n.changeLanguage(getClaudeI18nLanguage(selectedLanguage));
    }
  }, [selectedLanguage, claudeI18n]);

  const setSelectedLanguage = (language: string) => {

    onActiveLanguageChange?.(language);
    claudeI18n.changeLanguage(getClaudeI18nLanguage(language));
    onChangeConfig?.({
      ...currentConfig,
      language,
    });
  };

  if (!claudeData) {
    return <LoadingBlock />;
  }

  const selectedContent = content.find((item) => item.language === selectedLanguage);

  const onChangeChecked = (field: string, value: boolean | number[]) => {
    onChangeConfig?.({ ...currentConfig, [field]: value });
  };

  const renderFlag = (language: string) => {
    const countryCode = getClaudeLanguageCountryCode(language);
    const isEnglish = isClaudeEnglishLanguage(language);

    if (isEnglish) {
      return (
        <img
          src={imgUs}
          alt="US"
          className="mr-5"
          style={{ width: 14 }}
        />
      );
    }

    return (
      <ReactCountryFlag svg countryCode={countryCode} className="mr-5" style={{ width: 14 }} />
    );
  };

  const renderTextBlock = (label: string, value: string) => {
    if (!value) {
      return null;
    }

    return (
      <div className="mb-20 text-lh">
        <div className="text-bold">{label}</div>
        <div
          className={checkAHAVI(value) ? 'rtl' : 'ltr'}
          dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(value) }}
        />
      </div>
    );
  };

  const renderListItems = (items?: string[]) => {
    if (!items?.length) {
      return null;
    }

    return (
      <div id="text-fac">
        {items.map((item, index) => (
          <div key={index} className={checkAHAVI(item) ? 'rtl' : 'ltr'}>
            {item}
          </div>
        ))}
      </div>
    );
  };

  const renderFacilitiesList = (items?: string[]) => {
    if (!items?.length) {
      return null;
    }

    return (
      <div id="text-fac" className="row">
        {items.map((item, index) => (
          <div key={index} className={`col-4 ${checkAHAVI(item) ? 'rtl' : 'ltr'}`}>
            {item}
          </div>
        ))}
      </div>
    );
  };

  const checkIn = formatClaudeTextValue(selectedContent?.policies?.checkin);
  const checkOut = formatClaudeTextValue(selectedContent?.policies?.checkout);
  const pets = formatClaudeTextValue(selectedContent?.policies?.pets);
  const cancellation = formatClaudeTextValue(selectedContent?.policies?.cancellation);
  const adultsOnly = formatClaudeTextValue(selectedContent?.policies?.adultsOnly);
  const summary = selectedContent?.summary || '';
  const hasDetails =
    !!selectedContent?.name ||
    !!selectedContent?.location?.address ||
    !!checkIn ||
    !!checkOut ||
    !!pets ||
    !!cancellation ||
    !!adultsOnly ||
    !!selectedContent?.contact?.phone ||
    !!selectedContent?.contact?.website ||
    !!selectedContent?.rating?.overall_10 ||
    !!selectedContent?.amenities?.length;

  return (
    <div className="tripadvisor-data">
      <I18nextProvider i18n={i18nClaude}>
        <div className="flex-row mt-10 mb-10" dir="ltr">
          {content.map((item) => (
            <div className="ph-10" key={item.language}>
              <button
                onClick={() => setSelectedLanguage(item.language)}
                className={
                  'btn btn-default btn-xsm' +
                  (selectedLanguage === item.language ? ' btn-active-blue' : '')
                }
              >
                {renderFlag(item.language)}
                {getClaudeLanguageShortLabel(item.language)}
              </button>
            </div>
          ))}
        </div>

        <div dir={isRtl ? 'rtl' : 'ltr'}>
          {!selectedContent && (
            <div className="mt-20 text-bold">{t('claude.not_found')}</div>
          )}

          {!!selectedContent && (
            <div
              className="hotel-result-settings-content"
              style={{ backgroundColor: 'transparent', height: 'calc(100% - 30px)' }}
            >
              <div className="text-sm">
                <div className="mb-10">{t('proposal.add_to')}</div>
                {hasDetails && (
                  <Checkbox
                    className="mb-5"
                    checked={currentConfig.details}
                    onChange={() => onChangeChecked('details', !currentConfig.details || false)}
                    label={t('hotel.details')}
                  />
                )}
                {(!!summary || !!selectedContent.notes?.length) && (
                  <Checkbox
                    className="mb-5"
                    checked={currentConfig.overview}
                    onChange={() => onChangeChecked('overview', !currentConfig.overview || false)}
                    label={t('hotel.overview')}
                  />
                )}
                {(!!selectedContent.pros?.length || !!selectedContent.cons?.length) && (
                  <Checkbox
                    className="mb-20"
                    checked={currentConfig.prosCons}
                    onChange={() => onChangeChecked('prosCons', !currentConfig.prosCons || false)}
                    label={t('claude.pros_cons')}
                  />
                )}
              </div>
              <div className="details flex-column" style={{ width: '100%' }}>
                {hasDetails && (
                  <div className="default-shadow details-block">
                    <div>
                      <div className="title-underline text-lg mb-20 title-header-grey">
                        {t('hotel.details')}
                      </div>
                      {renderTextBlock(t('hotel.name'), selectedContent.name)}
                      {renderTextBlock(t('hotel.address'), selectedContent.location?.address)}
                      {renderTextBlock(t('hotel.area'), selectedContent.location?.area)}
                      {renderTextBlock(t('search.check_in'), checkIn)}
                      {renderTextBlock(t('search.check_out'), checkOut)}
                      {renderTextBlock(t('claude.pets'), pets)}
                      {renderTextBlock(t('claude.cancellation'), cancellation)}
                      {renderTextBlock(t('claude.adults_only'), adultsOnly)}
                      {renderTextBlock(t('hotel.phone'), selectedContent.contact?.phone)}
                      {renderTextBlock(t('claude.website'), selectedContent.contact?.website)}
                      {!!selectedContent.rating?.overall_10 && (
                        <div className="mb-20 text-lh">
                          <div className="text-bold">{t('hotel.rating')}</div>
                          <div>
                            {selectedContent.rating.overall_10}/10
                            {!!selectedContent.rating.totalReviews &&
                              ` (${selectedContent.rating.totalReviews} ${t('hotel.reviews')})`}
                          </div>
                        </div>
                      )}
                      {!!selectedContent.amenities?.length && (
                        <div className="text-lh pb-20">
                          <div className="title-underline text-lg mb-20">{t('hotel.facilities')}</div>
                          {renderFacilitiesList(selectedContent.amenities)}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!!selectedContent.images?.length && (
                  <div className="default-shadow details-block">
                    <div className="pr-photos-wr">
                      {selectedContent.images.map((image, index) => (
                        <div key={index} className="h-photos position-relative">
                          <img
                            src={image.link}
                            alt={image.title || '1'}
                            onError={(e) => ((e.target as any).parentNode.style.display = 'none')}
                            style={{ pointerEvents: 'none' }}
                          />
                          <div className="pr-photos-checkbox">
                            <div

                              onClick={() => {
                                const isUnchecking = imagesIndexes.includes(index);
                                const newIndexes = isUnchecking
                                  ? imagesIndexes.filter((i) => i !== index)
                                  : [...imagesIndexes, index];
                                if (!isUnchecking && newIndexes.length > 3) {
                                  showWarning(t('max_images_selected'));
                                  return;
                                }
                                onChangeChecked('imagesIndexes', newIndexes);
                              }}
                              className={`image-checkbox ${imagesIndexes.includes(index) ? 'selected' : ''}`}
                            >
                              {imagesIndexes.includes(index) && (
                                <FontAwesomeIcon icon={faCheck} size="xs" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(!!summary || !!selectedContent.notes?.length) && (
                  <div className="default-shadow details-block">
                    <div className="title-underline title-header-grey text-lg mb-20">
                      {t('hotel.overview')}
                    </div>
                    {!!summary && (
                      <div
                        className={'text-lh mb-20' + (checkAHAVI(summary) ? ' rtl' : ' ltr')}
                        dangerouslySetInnerHTML={{ __html: replaceHtmlToStr(summary) }}
                      />
                    )}
                    {!!selectedContent.notes?.length && (
                      <div className="text-lh">
                        <div className="text-bold mb-10">{t('claude.notes')}</div>
                        {renderListItems(selectedContent.notes)}
                      </div>
                    )}
                  </div>
                )}

                {(!!selectedContent.pros?.length || !!selectedContent.cons?.length) && (
                  <div className="default-shadow details-block">
                    <div className="text-lh pb-20">
                      {!!selectedContent.pros?.length && (
                        <>
                          <div className="title-underline text-lg mb-20">{t('claude.pros')}</div>
                          {renderListItems(selectedContent.pros)}
                        </>
                      )}
                      {!!selectedContent.cons?.length && (
                        <>
                          <div className={'title-underline text-lg mb-20' + (selectedContent.pros?.length ? ' mt-20' : '')}>
                            {t('claude.cons')}
                          </div>
                          {renderListItems(selectedContent.cons)}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </I18nextProvider>
    </div>
  );
};

export default ProposalClaudeHotelInformation;
